
import { useState } from "react";
import { ClientFormData } from "@/types/client";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useNavigate } from "react-router-dom";

export const useClientForm = () => {
  const [formData, setFormData] = useState<ClientFormData>({
    clientName: "",
    industry: "",
    meetingContext: "",
    serviceUsed: "",
    serviceOutcome: "",
    appreciation: "",
    emailTone: "Professional",
    desiredAction: "Reply to email",
    lastContactDate: new Date().toISOString().split('T')[0]
  });

  const [isGenerating, setIsGenerating] = useState(false);
  const [date, setDate] = useState<Date>();
  const { toast } = useToast();
  const navigate = useNavigate();

  const handleFormDataChange = (newData: Partial<ClientFormData>) => {
    setFormData(prev => ({
      ...prev,
      ...newData
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!date) {
      toast({
        title: "Missing Information",
        description: "Please select a last contact date.",
        variant: "destructive",
      });
      return;
    }

    setIsGenerating(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { error } = await supabase.from("clients").insert({
        user_id: user.id,
        client_name: formData.clientName,
        industry: formData.industry,
        meeting_context: formData.meetingContext,
        service_used: formData.serviceUsed,
        service_outcome: formData.serviceOutcome,
        appreciation: formData.appreciation,
        email_tone: formData.emailTone,
        desired_action: formData.desiredAction,
        last_contact_date: date.toISOString().split('T')[0],
      });

      if (error) throw error;

      toast({
        title: "Success",
        description: "Client added successfully!",
      });

      navigate("/client-directory");
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to add client. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsGenerating(false);
    }
  };

  return {
    formData,
    handleFormDataChange,
    isGenerating,
    handleSubmit,
    date,
    setDate
  };
};
